import { Check, Minus, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';
import { VisionBlocker } from '@/services/VisionMonitorService';

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  success: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  synced: 'bg-blue-100 text-blue-800',
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        STATUS_STYLES[status.toLowerCase()] ?? 'bg-gray-100 text-gray-800',
      )}
    >
      {status}
    </span>
  );
}

// `null` renders as "not set" (stock is a nullable column).
export function BoolCell({ value }: { value: boolean | null }) {
  if (value === null) {
    return (
      <Minus className="mx-auto h-4 w-4 text-gray-400" aria-label="Not set" />
    );
  }

  return value ? (
    <Check className="mx-auto h-4 w-4 text-green-600" aria-label="Yes" />
  ) : (
    <X className="mx-auto h-4 w-4 text-red-600" aria-label="No" />
  );
}

interface ErrorCellProps {
  title: string;
  message: string | null;
}

export function ErrorCell({ title, message }: ErrorCellProps) {
  if (!message) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="max-w-[240px] truncate text-left text-xs text-red-700 underline-offset-2 hover:underline"
          title="Click to see the full message"
        >
          {message}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-all rounded bg-gray-50 p-3 text-xs">
          {message}
        </pre>
      </DialogContent>
    </Dialog>
  );
}

const BLOCKER_LABELS: Record<VisionBlocker, string> = {
  OUT_OF_STOCK: 'Out of stock',
  INACTIVE: 'Inactive',
  NO_NAME: 'No name',
  NO_IMAGE: 'No image',
  CATEGORY_NOT_READY: 'Category not ready',
  CUSTOMER_CATEGORY_UPDATE_OFF: 'Customer: category update off',
  CUSTOMER_NO_TAXONOMY: 'Customer: no taxonomy',
  CUSTOMER_AUTO_RUN_OFF: 'Customer: vision auto-run off',
};

export function BlockersCell({ blockers }: { blockers: VisionBlocker[] }) {
  if (blockers.length === 0) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex max-w-[260px] flex-wrap gap-1">
      {blockers.map((blocker) => (
        <span
          key={blocker}
          className="rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-800"
        >
          {BLOCKER_LABELS[blocker]}
        </span>
      ))}
    </div>
  );
}

export function formatDateTime(value: string | null) {
  return value ? new Date(value).toLocaleString() : '—';
}

// releaseDate is a free-form string; ISO values are shortened to the date.
export function formatReleaseDate(value: string | null) {
  if (!value) return '—';

  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : value;
}
